import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How often should I get an oil change?',
      answer: 'For most modern vehicles, we recommend oil changes every 5,000 to 7,500 kilometers or every 6 months, whichever comes first. However, this can vary based on your vehicle make, model, driving habits, and the type of oil used. Check your owner\'s manual for manufacturer recommendations, and our technicians can help determine the best schedule for your specific vehicle.'
    },
    {
      question: 'When should I replace my brake pads?',
      answer: 'Brake pads typically last between 40,000 to 100,000 kilometers depending on driving conditions and habits. Warning signs include squealing or grinding noises, vibration when braking, or if your brake warning light comes on. We recommend having your brakes inspected at least once a year or if you notice any unusual sounds or performance issues.'
    },
    {
      question: 'How do I know if my vehicle needs new tires?',
      answer: 'You should replace your tires when the tread depth reaches 4/32 of an inch (use the penny test). Also look for uneven wear, cracks, bulges, or if they\'re more than 6 years old regardless of tread depth. We offer free tire inspections and can check tread depth, alignment, and overall tire condition.'
    },
    {
      question: 'What does a check engine light mean?',
      answer: 'A check engine light can indicate various issues from a loose gas cap to serious engine problems. We use advanced computer diagnostics to read the error codes and identify the exact issue. Don\'t ignore it - bring your vehicle in for a diagnostic scan. We\'ll explain the problem and provide repair options with upfront pricing.'
    },
    {
      question: 'How often should I get a wheel alignment?',
      answer: 'We recommend checking your alignment once a year or every 15,000-20,000 kilometers. You should also get it checked if you notice uneven tire wear, your vehicle pulling to one side, or after hitting a pothole or curb. Proper alignment extends tire life and improves fuel efficiency.'
    },
    {
      question: 'Do you work with insurance companies for collision repairs?',
      answer: 'Yes! We work directly with all major insurance companies in Ontario. We can handle the entire claims process, provide detailed estimates, and offer direct billing. Our certified technicians perform collision repairs to manufacturer standards, and all work comes with our warranty.'
    },
    {
      question: 'What maintenance does my vehicle need for winter?',
      answer: 'Winter preparation includes: battery test and replacement if needed, antifreeze check, winter tire installation, brake inspection, wiper blade replacement, and ensuring all fluids are at proper levels. We offer complete winter maintenance packages to keep you safe on icy roads.'
    },
    {
      question: 'How long will my repair take?',
      answer: 'Repair time varies by service. Oil changes typically take 30-45 minutes, brake service 1-3 hours, and major repairs or collision work can take several days. We provide time estimates upfront and offer towing, pick-up/drop-off service, and loaner vehicles to minimize inconvenience.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-[#0a0a0a]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-wider mb-3 block">
            Frequently Asked Questions
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Your Auto Repair Questions Answered
          </h2>
          <div className="h-1 w-24 bg-[#ff6b35] mx-auto rounded-full"></div>
          <p className="text-gray-400 text-lg mt-6">
            Expert answers to common vehicle maintenance and repair questions
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#151515] border border-white/10 rounded-xl overflow-hidden hover:border-[#ff6b35]/30 transition-all duration-300"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left transition-all duration-200"
              >
                <h3 className="text-lg font-semibold text-white pr-8">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-[#ff6b35]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Answer */}
              {openIndex === index && (
                <div className="px-6 pb-6 pt-0">
                  <div className="h-px bg-white/5 mb-4"></div>
                  <p className="text-gray-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-3">
            Still Have Questions?
          </h3>
          <p className="text-gray-400 mb-6">
            Our friendly team is here to help. Give us a call or stop by our shop.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:416-747-6800"
              className="inline-flex items-center justify-center bg-[#ff6b35] hover:bg-[#e55a28] text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Call: 416-747-6800
            </a>
            <a
              href="mailto:7476800@gmail.com"
              className="inline-flex items-center justify-center border-2 border-white/20 bg-white/5 hover:bg-white/10 hover:border-[#ff6b35] text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
